import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ListItem } from '../../../../models/api/core/list/list-item';
import { AlertsService } from '../../../../services/alerts/alerts.service';
import { BaseComponent } from '../../../base/base.component';
import { PickListComponent } from '../../../core/controls/pick-list.component';
import { SelectComponent } from '../../../core/controls/select.component';

@Component({
  selector: 'app-pay-code-option',
  imports: [PickListComponent, SelectComponent],
  template: `
    <div class="tw-grow tw-flex tw-flex-col">
      <div class="tw-flex tw-items-center tw-gap-4 tw-pb-2">
        <span class="tw-font-bold">Series</span>
        <app-select
          class="tw-w-[260px]"
          [options]="seriesOptions"
          optionLabel="description"
          optionValue="value"
          [control]="seriesControl"
          [multiple]="true"
          [showClear]="true"
          [filter]="false"
          [virtualScroll]="false"
          [showHeader]="false"
          label="Series"
        />
      </div>
      <span class="tw-grow tw-font-bold tw-pt-2 tw-pb-6"
        >The payment code is</span
      >
      <app-pick-list
        [sourceControl]="availableControl"
        [targetControl]="control"
        labelName="label"
        keyName="value"
        helpText="Select up to 10 pay codes"
        (targetChanged)="onTargetChanged($event)"
        [width]="pickListWidth"
      />
    </div>
  `,
  styles: ``,
})
export class PayCodeOptionComponent extends BaseComponent implements OnInit {
  alerts = inject(AlertsService);
  private elementRef = inject(ElementRef);

  @Input()
  control: FormControl<any[]>;

  @Input()
  availableControl: FormControl<any[]>;

  @Input()
  lobControl: FormControl<string[]>;

  seriesControl = new FormControl<string[]>([]);

  pickListWidth = 700;

  seriesOptions: ListItem[] = [
    { description: '100 Series (Indemnity/Loss)', value: '100' },
    { description: '200 Series (Loss)', value: '200' },
    { description: '300 Series (Medical)', value: '300' },
    { description: '400 Series (Loss)', value: '400' },
    { description: '500 Series (Loss)', value: '500' },
    { description: '600 Series (Expense)', value: '600' },
    { description: '700 Series (Recovery)', value: '700' },
  ];

  @HostListener('window:resize')
  onResize(): void {
    this.pickListWidth = this.getPickListWidth();
  }

  async ngOnInit() {
    this.pickListWidth = this.getPickListWidth();
    this.control.setValidators([Validators.required]);
    if (this.control.value == null) {
      this.control.setValue([]);
    }
    this.control.updateValueAndValidity({ emitEvent: false });

    this.dispose.subs = this.lobControl?.valueChanges.subscribe(() => {
      this.loadPayCodes();
    });

    this.dispose.subs = this.seriesControl.valueChanges.subscribe(() => {
      this.applySeriesFilter();
    });

    await this.loadPayCodes();
  }

  onTargetChanged(items: any[]) {
    if (items.length <= 10) {
      return;
    }

    const trimmed = items.slice(0, 10);
    this.control.setValue(trimmed);
  }

  private async loadPayCodes() {
    const lobs = this.lobControl?.value || [];
    await this.alerts.loadPayCodes(lobs);
    this.removeInvalidSelections();
    this.applySeriesFilter();
    this.syncSelectedLabels();
  }

  private removeInvalidSelections() {
    const selected = this.control.value || [];
    if (selected.length === 0) {
      return;
    }

    const validValues = new Set<string>();
    for (const group of this.alerts.payCodeTypes || []) {
      for (const item of group.items || []) {
        validValues.add(item.value?.toString());
      }
    }

    const filtered = selected.filter((item: any) => {
      const value = item.value?.toString() ?? item?.toString();
      return validValues.has(value);
    });

    if (filtered.length !== selected.length) {
      this.control.setValue(filtered);
    }
  }

  private applySeriesFilter() {
    const selectedSeries = this.seriesControl.value || [];
    const source = this.alerts.payCodeTypes || [];

    if (selectedSeries.length === 0) {
      this.availableControl.setValue(source);
      return;
    }

    const filtered = source.filter((group) => {
      // Extract series number from label (e.g., "100 Series (Indemnity/Loss)" -> "100")
      const match = group.label?.match(/^(\d+)\s+Series/);
      if (!match) {
        return false;
      }
      const seriesNumber = match[1];
      return selectedSeries.includes(seriesNumber);
    });
    this.availableControl.setValue(filtered);
  }

  private syncSelectedLabels() {
    const selected = this.control.value || [];
    if (selected.length === 0) {
      return;
    }

    const lookup = new Map<string, string>();
    for (const group of this.alerts.payCodeTypes || []) {
      for (const item of group.items || []) {
        lookup.set(item.value?.toString(), item.label);
      }
    }

    const mapped = selected.map((item: any) => ({
      ...item,
      label: lookup.get(item.value?.toString()) ?? item.label ?? item.value,
    }));
    this.control.setValue(mapped);
  }

  private getPickListWidth(): number {
    if (typeof window === 'undefined') {
      return 700;
    }

    const padding = 48;
    const containerEl = this.elementRef?.nativeElement?.parentElement;
    const containerW = containerEl?.offsetWidth ?? 0;
    const viewportMax = window.innerWidth - padding;
    const containerMax = containerW > 0 ? containerW - 24 : viewportMax;
    return Math.max(700, Math.min(viewportMax, containerMax));
  }
}
